import { FC, useCallback, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { BrowserRouter } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import * as Sentry from '@sentry/browser';
import {
	Login,
	Register,
	Overview,
	VerifyIdentity,
	Verify2FA,
	Settings,
	ForgotPasswordResetPassword,
	ForgotPassword,
	Login2FA,
	AutoLogin,
	AML,
	Account,
	AccountDeletingConfirmation,
	Funding,
	Deposit,
	Withdrawal,
	Statement,
	Settings2FAOff,
	SecondaryMarket,
	NotFound,
	MyInvestments,
	PrimaryMarket,
	PrimaryMarketProject,
	KYC,
	TaxResidency,
	SaleProject,
	News,
	PieceOfNews,
	QuestionnaireSuccess,
	VerifyIdentityFailed,
} from 'pages';
import { ScrollToTop, AuthRoute } from 'componets/common';
import { Loading, RejectedMessage } from 'componets/ui';
import { getUserProfile } from 'features/user/actions';
import { getGlobals, getMenu } from 'features/settings/actions';
import {initAuth, setAuthError } from 'features/authorization/authorizationSlice';
import { getBalance } from './features/overview/actions';
import {
	VerificationStatus,
	ClientStatus,
	Paths,
	ACCESS_TOKEN,
	SUPPORTED_LANGUAGES,
	REFRESH_TOKEN,
} from 'utils/constants';
import { AppDispatch, store } from 'store';
import { getLanguage } from 'utils';
import { useAppSelector, useCloseAntMessage } from 'hooks';
import Utils from './utils/utils';
import { logout } from './features/authorization/actions';
import {diffNow} from "./utils/DateTime";
import ViewInvestment from "./pages/Account/MyInvestments/ViewInvestment";
import {LoginOTP} from "./pages/Login/LoginOTP";

const tagManagerArgs = {
	gtmId: 'GTM-NPNJ4DD',
};

if (process.env.NODE_ENV === 'production') {
	TagManager.initialize(tagManagerArgs);
}

export const onForbidden = () => {
	Cookies.remove(ACCESS_TOKEN);
	Cookies.remove(REFRESH_TOKEN);
	store.dispatch(setAuthError(true));
	store.dispatch(logout());
};

const App: FC = () => {
	const user = useAppSelector((state) => state.user.entity);
	const { authError, tfaToken, otp } = useAppSelector((state) => state.authorization);

	const [loading, setLoading] = useState(true);

	const dispatch = useDispatch<AppDispatch>();

	const getInitialData = useCallback(async () => {
		const token = Cookies.get(ACCESS_TOKEN);
		if (token) {
			dispatch(initAuth({ access_token: token }));
			const allAuthPromise = Promise.all([dispatch(getUserProfile()), dispatch(getBalance())]);

			await allAuthPromise;
		}

		const allPromise = Promise.all([dispatch(getGlobals()), dispatch(getMenu())]).catch(() => {});

		await allPromise;
	}, [dispatch]);

	useEffect(() => {
		if (authError) {
			dispatch(setAuthError(false));
		}
	}, [dispatch]);

	useEffect(() => {
		getInitialData().finally(() => {
			setLoading(false);
		});
	}, [getInitialData]);

	useEffect(() => {
		Sentry.setUser({
			id: user?.accountId,
		});
	}, [user.accountId]);

	const language = getLanguage();

	useEffect(() => {
		if (language) {
			const langPath = window.location.pathname.split('/')[1];

			if (langPath !== language) {
				const isNotLang = SUPPORTED_LANGUAGES.indexOf(langPath) === -1;
				window.location.href = isNotLang
					? `${window.location.origin}/${language}${window.location.pathname}`
					: `${window.location.pathname.replace(langPath, language)}`;
			}
		}
	}, [dispatch, language]);

	useEffect(() => {
		const { frontChatHash = null, email = null, firstName = '', lastName = '', accountId } = user;
		const { FrontChat = null }:any = window;
		let payload = {};
		if (frontChatHash) {
			payload = {
				email: email,
				name: `${firstName} ${lastName}`,
				userHash: frontChatHash,
				customFields: {
					'clientId': accountId,
				}
			}
		}
		if (FrontChat && process.env.REACT_APP_FRONT_CHAT_ID) {
			console.debug('init front chat', payload);
			FrontChat('init', { chatId: process.env.REACT_APP_FRONT_CHAT_ID, useDefaultLauncher: true, ...payload });
		}
	}, [user]);

	useCloseAntMessage();

	if (loading) return <Loading />;

	const isActiveUser = user.status === ClientStatus.ACTIVE;
	const isIncompleteUser = user.status === ClientStatus.INCOMPLETE;
	const basename = `/${getLanguage()}${process.env.PUBLIC_URL || ''}`;

	return (
		<>
			{/* workaround solution to show 404 outside basename */}
			{!new RegExp(`^${basename}`, 'i').test(window.location.pathname) && (
				<div className="basename-fallback">
					<RejectedMessage isNotFound />
				</div>
			)}
			<BrowserRouter basename={basename}>
				<ScrollToTop />

				<Routes>
					<Route
						path="/"
						element={
							<AuthRoute isAllowed={!user.accountId}>
								<Login />
							</AuthRoute>
						}
					/>

					<Route
						path={Paths.FORGOT_PASSWORD}
						element={
							<AuthRoute isAllowed={!user.accountId}>
								<ForgotPassword />
							</AuthRoute>
						}
					/>

					<Route path="/autologin" element={<AutoLogin />} />

					<Route
						path={`${Paths.FORGOT_PASSWORD}/:token`}
						element={
							<AuthRoute isAllowed={true}>
								<ForgotPasswordResetPassword />
							</AuthRoute>
						}
					/>

					<Route
						path="/login/2fa"
						element={
							<AuthRoute isAllowed={!!tfaToken && !user.accountId}>
								<Login2FA />
							</AuthRoute>
						}
					/>

					<Route
						path={Paths.LOGIN_OTP}
						element={
							<AuthRoute isAllowed={!user.accountId && !!otp}>
								<LoginOTP />
							</AuthRoute>
						}
					/>

					<Route
						path={Paths.VERIFY_IDENTITY}
						element={
							<AuthRoute
								isAllowed={isActiveUser && user.verificationStatus !== VerificationStatus.VERIFIED}
							>
								<VerifyIdentity />
							</AuthRoute>
						}
					/>

					<Route
						path={Paths.VERIFY_IDENTITY_FAILED}
						element={
							<AuthRoute
								isAllowed={isActiveUser && user.verificationStatus === VerificationStatus.FAILED}
								redirectPath={Utils.fundingRedirect(user)}
							>
								<VerifyIdentityFailed />
							</AuthRoute>
						}
					/>

					<Route
						path={Paths.RESIDENCY}
						element={
							<AuthRoute isAllowed={isActiveUser && (!user.taxResidence || process.env.NODE_ENV === 'development')}>
								<TaxResidency />
							</AuthRoute>
						}
					/>

					<Route path={Paths.SETTINGS}>
						<Route
							path=""
							element={
								<AuthRoute isAllowed={isActiveUser}>
									<Settings />
								</AuthRoute>
							}
						/>
						<Route
							path="2fa"
							element={
								<AuthRoute isAllowed={isActiveUser}>
									<Verify2FA />
								</AuthRoute>
							}
						/>
						<Route
							path="2fa/off"
							element={
								<AuthRoute isAllowed={isActiveUser}>
									<Settings2FAOff />
								</AuthRoute>
							}
						/>
					</Route>

					<Route
						path={Paths.REGISTER}
						element={
							<AuthRoute isAllowed={isIncompleteUser || !user.accountId}>
								<Register />
							</AuthRoute>
						}
					/>

					<Route path="/delete-account/:token" element={<AccountDeletingConfirmation />} />

					<Route
						path="/account"
						element={
							<AuthRoute isAllowed={isActiveUser}>
								<Account />
							</AuthRoute>
						}
					>
						<Route
							path="overview"
							element={
								<AuthRoute isAllowed={isActiveUser}>
									<Overview />
								</AuthRoute>
							}
						/>
						<Route path="funding" element={<Navigate to={Paths.FUNDING_DEPOSIT} replace />} />
						<Route
							element={
								<AuthRoute
									isAllowed={
										isActiveUser &&
										user.verificationStatus === VerificationStatus.VERIFIED &&
										user.taxResidence !== null &&
										!!user.riskTolerance?.value &&
										Utils.checkYearsDifferent(user.riskTolerance?.date || '', 2)
									}
									redirectPath={Utils.fundingRedirect(user)}
								>
									<Funding />
								</AuthRoute>
							}
						>
							<Route path="deposit" element={<Deposit />} />
							<Route path="withdraw" element={<Withdrawal />} />
						</Route>

						<Route path="statement">
							<Route index element={<Navigate to="account-summary" replace />} />
							<Route
								path="account-summary"
								element={
									<AuthRoute isAllowed={isActiveUser}>
										<Statement />
									</AuthRoute>
								}
							/>
							<Route
								path="transaction"
								element={
									<AuthRoute isAllowed={isActiveUser}>
										<Statement />
									</AuthRoute>
								}
							/>
							<Route
								path="tax-statements"
								element={
									<AuthRoute isAllowed={isActiveUser}>
										<Statement />
									</AuthRoute>
								}
							/>
						</Route>

						<Route
							path="my-investments"
							element={
								<AuthRoute isAllowed={isActiveUser}>
									<MyInvestments />
								</AuthRoute>
							}
						/>

						<Route
							path="investments/:investmentId"
							element={
								<AuthRoute isAllowed={isActiveUser}>
									<ViewInvestment />
								</AuthRoute>
							}
						/>

						<Route
							path="my-investments/sale/:investmentId"
							element={
								<AuthRoute isAllowed={isActiveUser}>
									<SaleProject />
								</AuthRoute>
							}
						/>

						<Route
							path="invest/primarymarket"
							element={
								<AuthRoute isAllowed={isActiveUser}>
									<PrimaryMarket />
								</AuthRoute>
							}
						/>

						<Route
							path="invest/secondarymarket"
							element={
								<AuthRoute isAllowed={isActiveUser}>
									<SecondaryMarket />
								</AuthRoute>
							}
						/>

						<Route
							path="projects/:projectId"
							element={
								<AuthRoute isAllowed={isActiveUser}>
									<PrimaryMarketProject />
								</AuthRoute>
							}
						/>

						<Route
							path="questionnaire/kyc"
							element={
								<AuthRoute
									isAllowed={isActiveUser && (
										!user.quizStatuses?.KYC.status ||
										(user.quizStatuses?.KYC.date ? diffNow(user.quizStatuses.KYC.date, 'months') >= 23 : false)
									)}
									redirectPath={Paths.AML}
								>
									<KYC />
								</AuthRoute>
							}
						/>

						<Route
							path="questionnaire/aml"
							element={
								<AuthRoute isAllowed={
									isActiveUser && (
										!user.quizStatuses?.AML.status ||
										(user.quizStatuses?.AML.date ? diffNow(user.quizStatuses.AML.date, 'months') >= 23 : false)
									)
								}>
									<AML />
								</AuthRoute>
							}
						/>

						<Route
							path="questionnaire/success"
							element={
								<AuthRoute isAllowed={
									isActiveUser && (
										!!user.quizStatuses?.KYC.status && !!user.quizStatuses?.AML.status
									)
								}>
									<QuestionnaireSuccess />
								</AuthRoute>
							}
						/>

						<Route
							path="news"
							element={
								<AuthRoute isAllowed={isActiveUser}>
									<News />
								</AuthRoute>
							}
						/>

						<Route
							path="news/:newsUuid"
							element={
								<AuthRoute isAllowed={isActiveUser}>
									<PieceOfNews />
								</AuthRoute>
							}
						/>

						<Route path="*" element={<RejectedMessage isNotFound />} />
					</Route>

					<Route path="*" element={<NotFound />} />
				</Routes>
			</BrowserRouter>
		</>
	);
};

export default App;
